import { stopImpersonating } from "$root/functional-areas/user/auth";
import { impersonateEmail$ } from "$root/functional-areas/session/atom";
import { useUserValue } from "$root/functional-areas/user";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { Icon, Text } from "@mdotm/shadcn/legacy/components";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useHistory } from "react-router";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";

/**
 * Global, always-visible indicator shown while operating as another user
 * (impersonation). Rendered near the app root so it spans every route. Provides
 * an "Exit" affordance that restores the impersonator's own session.
 */
export function ImpersonationBanner(): React.JSX.Element | null {
	const user = useUserValue();
	const impersonateEmail = useAtomValue(impersonateEmail$);
	const history = useHistory();

	if (!user.signedIn || !impersonateEmail) {
		return null;
	}

	async function exitImpersonation() {
		await runWithErrorReporting(
			async () => {
				await stopImpersonating();
				history.push("/");
			},
			{ area: "user", attemptedOperation: "stop impersonating user" },
		);
	}

	return (
		<div
			className="fixed top-2 left-1/2 -translate-x-1/2 z-[1500] flex items-center gap-2 rounded-md px-4 py-1.5 shadow-lg"
			style={{ background: themeCSSVars.palette_N700, color: "white" }}
			data-qualifier="ImpersonationBanner"
		>
			<Icon icon="Top-menu-user" size={16} color="white" />
			<Text type="Body/M/Bold" as="span" style={{ color: "white" }}>
				Operating as {impersonateEmail}
			</Text>
			<Button
				palette="ghost"
				size="x-small"
				onClickAsync={exitImpersonation}
				className="ml-1 flex items-center gap-1 bg-white/20 py-0.5 hover:bg-white/30"
				data-qualifier="ImpersonationBanner/Exit"
			>
				<Icon icon="Close" size={12} color="white" />
				<Text type="Body/S/Bold" as="span" style={{ color: "white" }}>
					Exit
				</Text>
			</Button>
		</div>
	);
}
